/**
 * Draw active projectile bullets as small ',' glyphs with a short bloom
 * trail. One sprite per bullet, reused across frames by bullet id. Dead
 * bullets have their sprites removed.
 */

import { Container, Sprite, Texture } from 'pixi.js';
import type { Bullet } from '../sim/bullet';
import type { GlyphAtlas } from './atlas';
import { BULLET_GLYPH, BULLET_TRAIL_ALPHA, BULLET_TRAIL_PX, COLOR_WARN } from './style';

interface BulletEntry {
  glyph: Sprite;
  trail: Sprite;
}

export class BulletsLayer {
  readonly container: Container = new Container();
  private readonly sprites = new Map<number, BulletEntry>();
  private tileSize = 20;
  private atlas: GlyphAtlas | null = null;

  init(atlas: GlyphAtlas, tileSize: number): void {
    this.atlas = atlas;
    this.tileSize = tileSize;
    this.container.sortableChildren = true;
  }

  update(bullets: readonly Bullet[]): void {
    if (!this.atlas) return;
    const seen = new Set<number>();
    const tex = this.atlas.get(BULLET_GLYPH) ?? Texture.EMPTY;
    const scale = (this.tileSize / this.atlas.cellH) * 0.9;
    for (const b of bullets) {
      if (!b.alive) continue;
      seen.add(b.id);
      let e = this.sprites.get(b.id);
      if (!e) {
        const glyph = new Sprite(tex);
        glyph.anchor.set(0.5, 0.5);
        glyph.scale.set(scale);
        glyph.tint = COLOR_WARN;
        (glyph as unknown as { __layerIdx: number }).__layerIdx = 6;
        this.container.addChild(glyph);
        const trail = new Sprite(tex);
        trail.anchor.set(0.5, 0.5);
        trail.scale.set(scale * 1.6);
        trail.tint = COLOR_WARN;
        trail.alpha = BULLET_TRAIL_ALPHA;
        trail.blendMode = 'add';
        (trail as unknown as { __layerIdx: number }).__layerIdx = 5;
        this.container.addChild(trail);
        e = { glyph, trail };
        this.sprites.set(b.id, e);
      }
      const px = b.x * this.tileSize;
      const py = b.y * this.tileSize;
      e.glyph.x = px;
      e.glyph.y = py;
      // Trail sits behind the bullet along its velocity vector.
      const speed = Math.hypot(b.vx, b.vy) || 1;
      const off = BULLET_TRAIL_PX;
      e.trail.x = px - (b.vx / speed) * off;
      e.trail.y = py - (b.vy / speed) * off;
      // Rotate the trail to align with velocity — otherwise a stretched-
      // scale glyph looks off-axis. Base glyph is ',' which is asymmetric,
      // so rotation makes it read as a directional streak.
      const angle = Math.atan2(b.vy, b.vx);
      e.trail.rotation = angle;
      e.glyph.zIndex = Math.floor(b.y) * 32 + 6;
      e.trail.zIndex = e.glyph.zIndex - 1;
    }
    // Reap sprites for bullets no longer alive.
    for (const [id, entry] of this.sprites) {
      if (!seen.has(id)) {
        entry.glyph.destroy();
        entry.trail.destroy();
        this.sprites.delete(id);
      }
    }
  }
}
